// ──────────────────────────────────────────────────────────────
//  AppNetwork.ts — Typed network layer for every server endpoint.
//
//  Every function:
//   1. Builds the URL from `site.backend.api.*`
//   2. Calls `authFetch`/`authPost`/`authPut`/`authDelete` (or plain `fetch`)
//   3. Unwraps the `{ status, data }` envelope
//   4. Returns strongly-typed data
//
//  Unauthenticated endpoints (login, register, etc.) use raw `fetch`.
//  Authenticated endpoints use helpers that attach the JWT.
// ──────────────────────────────────────────────────────────────

import { get } from 'svelte/store';
import { getToken, preferredAuthMethod } from './Auth';
import site from './site';
import type {
	AnalyticsBatchRequest,
	AnalyticsBatchResponse,
	AnalyticsCatalog,
	AnalyticsConfiguration,
	AnalyticsConfigurationResponse,
	AnalyticsEventRow,
	AnalyticsFieldValue,
	AnalyticsQueryRequest,
	AnalyticsQueryResponse,
	APIResponse,
	Application,
	ApplicationPublicInfo,
	AuditListResponse,
	AuthorizeResult,
	AuthTokenResponse,
	CohortRetention,
	Configuration,
	DashboardConfig,
	DashboardStats,
	EmailTemplate,
	ImportUsersResult,
	License,
	LicenseActivation,
	LicenseApplicationOverview,
	LicenseBackendProbe,
	LicenseDownload,
	NewsFeedItem,
	Newsletter,
	NewsletterAnalytics,
	NewsletterRecipient,
	NewsletterStagingData,
	OAuthAuthorizeResult,
	PaginatedResponse,
	Passkey,
	Placeholder,
	Role,
	RoleReference,
	Session,
	SeatAssignmentInput,
	SessionLocationAnalytics,
	Settings,
	SMTPConfig,
	SortDirection,
	SupportReplyTemplate,
	SupportReplyTemplateInput,
	SupportCategory,
	SupportCategoryInput,
	SupportDeskQuery,
	SupportDeskStats,
	SupportDeskTicketInput,
	SupportOverview,
	SupportPerson,
	SupportRoutingMemberInput,
	SupportRoutingRule,
	SupportSettings,
	SupportTicket,
	SupportTicketInput,
	SupportTicketPatch,
	SupportTicketStatus,
	SupportTicketSummary,
	User,
	UserApplication,
	UserNotification
} from './AppTypes';

/** Resolved base URL for all API calls. */
const base = site.backend.baseURL;

/** Shorthand for `site.backend.api`. */
const api = site.backend.api;

// ── Shared validation ─────────────────────────────────────────

/** Regex for the local part of an email (before @). */
const LOCAL_RE = /^[a-zA-Z0-9_+-](?:[a-zA-Z0-9._+-]*[a-zA-Z0-9_+-])?$/;

/** Regex for the domain of an email (after @). */
const DOMAIN_RE =
	/^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

/**
 * Validates an email address against practical RFC 5321 rules.
 * Mirrors the server-side `isValidEmail()` in `Utilities.ts`.
 */
export function isValidEmail(email: string): boolean {
	if (!email || email.length > 254) return false;

	const atIndex = email.indexOf('@');
	/** Require exactly one @ sign that is not the first character. */
	if (atIndex < 1 || atIndex !== email.lastIndexOf('@')) return false;

	const local = email.slice(0, atIndex);
	const domain = email.slice(atIndex + 1);

	/** Enforce RFC 5321 local-part length limits. */
	if (local.length < 1 || local.length > 64) return false;
	/** Reject local parts with invalid characters. */
	if (!LOCAL_RE.test(local)) return false;
	/** Consecutive dots are not allowed in the local part. */
	if (local.includes('..')) return false;

	/** Validate the domain portion against allowed hostname format. */
	if (!DOMAIN_RE.test(domain)) return false;

	return true;
}

// ── Internal helpers ──────────────────────────────────────────

/**
 * Unwrap the standard `{ status, data }` response envelope.
 * Throws on non-2xx status or if the body cannot be parsed.
 *
 * **401 handling**: Token is expired or invalid — force logout and
 * redirect to the login page.
 *
 * **403 handling**: User is authenticated but lacks permission for
 * the requested resource.  Shows an error toast but does **not**
 * log the user out.
 *
 * **204 No Content**: Returns `undefined as T` since there is no body.
 */
async function unwrap<T>(response: Response): Promise<T> {
	if (!response.ok) {
		/* Expired / invalid token — force logout. */
		if (response.status === 401) {
			const { logout } = await import('./Auth');
			logout();
			throw new Error('Session expired. Please log in again.');
		}

		/* Permission denied — notify without disrupting the session. */
		if (response.status === 403) {
			const url = new URL(response.url, window.location.origin);
			const { toastError } = await import('./toast');
			toastError(`Access denied: ${url.pathname}`);
			throw new Error(`Forbidden: ${url.pathname}`);
		}

		/* Try to extract a server error message from the body. */
		let message = response.statusText;
		try {
			const body = await response.json();
			if (body?.data?.message) message = body.data.message;
			else if (body?.error) message = body.error;
			else if (body?.message) message = body.message;
		} catch {
			/* body wasn't JSON — keep statusText */
		}
		throw new Error(message);
	}

	/* 204 No Content — no body to parse. */
	if (response.status === 204) {
		return undefined as T;
	}

	const json: APIResponse<T> = await response.json();
	return json.data;
}

/**
 * Unwrap a paginated response envelope into `{ items, total }`.
 */
async function unwrapPaginated<T>(response: Response): Promise<PaginatedResponse<T>> {
	const data = await unwrap<PaginatedResponse<T>>(response);
	return data;
}

/**
 * Perform an authenticated GET request with the stored JWT.
 */
function authFetch(url: string): Promise<Response> {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	return fetch(url, {
		headers: { Authorization: `Bearer ${token}` },
		signal: AbortSignal.timeout(30_000)
	});
}

/**
 * Perform an authenticated POST request with JSON body.
 */
function authPost(url: string, body?: unknown): Promise<Response> {
	const token = getToken();
	/** Abort early if there is no stored JWT. */
	if (!token) throw new Error('Not authenticated');
	return fetch(url, {
		method: 'POST',
		headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
		body: body !== undefined ? JSON.stringify(body) : undefined,
		signal: AbortSignal.timeout(30_000)
	});
}

/**
 * Perform an authenticated file upload (multipart/form-data).
 * Do NOT set Content-Type — the browser adds the boundary automatically.
 */
function authUpload(url: string, formData: FormData): Promise<Response> {
	const token = getToken();
	/** Abort early if there is no stored JWT. */
	if (!token) throw new Error('Not authenticated');
	return fetch(url, {
		method: 'POST',
		headers: { Authorization: `Bearer ${token}` },
		body: formData,
		signal: AbortSignal.timeout(60_000)
	});
}

/** Receives an upload's progress, as the fraction of the request body sent so far (0–1). */
export type UploadProgressHandler = (_fraction: number) => void;

/**
 * Perform an authenticated multipart upload that reports its progress.
 *
 * `fetch` has no upload progress, so this goes through `XMLHttpRequest` — with the
 * same JWT header, the same FormData (no Content-Type: the browser adds the
 * boundary) and the same 60-second ceiling as {@link authUpload}.
 *
 * It resolves with a `Response`-shaped view of the XHR's result rather than the
 * parsed body, so callers hand it to {@link unwrap} exactly as they would a fetch
 * response: envelope parsing, error messages, the 403 toast and the 401
 * force-logout all stay in one place. A transport failure (network down, timeout,
 * abort) rejects, as a failed `fetch` would.
 *
 * Progress is reported only when the browser knows the total size
 * (`lengthComputable`); without it there is no fraction to report.
 */
function authUploadWithProgress(url: string, formData: FormData, onProgress?: UploadProgressHandler): Promise<Response> {
	const token = getToken();
	/** Abort early if there is no stored JWT. */
	if (!token) throw new Error('Not authenticated');
	return new Promise<Response>((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open('POST', url);
		xhr.setRequestHeader('Authorization', `Bearer ${token}`);
		xhr.timeout = 60_000;
		/* Registered only when someone listens: an upload listener can force a CORS preflight. */
		if (onProgress) {
			xhr.upload.onprogress = (event: ProgressEvent) => {
				/* Without a known total there is no fraction to report. */
				if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
			};
		}
		xhr.onload = () => resolve(xhrResponse(xhr, url));
		xhr.onerror = () => reject(new Error('Network error. Please check your connection and try again.'));
		xhr.ontimeout = () => reject(new Error('The upload timed out.'));
		xhr.onabort = () => reject(new Error('The upload was cancelled.'));
		xhr.send(formData);
	});
}

/**
 * The part of a `Response` that {@link unwrap} reads, built from a finished XHR.
 *
 * A plain object rather than `new Response(...)`: the constructor cannot carry the
 * request URL (which the 403 message names) and refuses a body on a 204.
 */
function xhrResponse(xhr: XMLHttpRequest, url: string): Response {
	const { status, statusText, responseText } = xhr;
	return {
		ok: status >= 200 && status < 300,
		status,
		statusText,
		url: xhr.responseURL || url,
		/* Parsed lazily, like `Response.json()`: `unwrap` tolerates a non-JSON error body. */
		json: () => Promise.resolve().then(() => JSON.parse(responseText))
	} as Response;
}

/**
 * Perform an authenticated PUT request with JSON body.
 */
function authPut(url: string, body?: unknown): Promise<Response> {
	const token = getToken();
	/** Abort early if there is no stored JWT. */
	if (!token) throw new Error('Not authenticated');
	return fetch(url, {
		method: 'PUT',
		headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
		body: body !== undefined ? JSON.stringify(body) : undefined,
		signal: AbortSignal.timeout(30_000)
	});
}

/**
 * Perform an authenticated PATCH request with JSON body.
 */
function authPatch(url: string, body: unknown): Promise<Response> {
	const token = getToken();
	/** Abort early if there is no stored JWT. */
	if (!token) throw new Error('Not authenticated');
	return fetch(url, {
		method: 'PATCH',
		headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
		body: JSON.stringify(body),
		signal: AbortSignal.timeout(30_000)
	});
}

/**
 * Hands a downloaded blob to the browser as a file save.
 *
 * Through a synthetic anchor, with the revoke deferred: revoking immediately can
 * cancel the download in some browsers, and the `finally` releases the blob URL
 * even if the click throws — otherwise a failed download leaks the whole file.
 */
function saveBlob(blob: Blob, filename: string): void {
	const url = URL.createObjectURL(blob);
	/* The revoke is deferred and in a `finally`, for the reasons above. */
	try {
		const anchor = document.createElement('a');
		anchor.href = url;
		anchor.download = filename;
		document.body.appendChild(anchor);
		anchor.click();
		anchor.remove();
	} finally {
		setTimeout(() => URL.revokeObjectURL(url), 10_000);
	}
}

/**
 * Perform an authenticated DELETE request, optionally with JSON body.
 */
function authDelete(url: string, body?: unknown): Promise<Response> {
	const token = getToken();
	/** Abort early if there is no stored JWT. */
	if (!token) throw new Error('Not authenticated');
	const options: RequestInit = {
		method: 'DELETE',
		headers: { Authorization: `Bearer ${token}` },
		signal: AbortSignal.timeout(30_000)
	};
	/** Attach a JSON body when one is provided. */
	if (body !== undefined) {
		(options.headers as Record<string, string>)['Content-Type'] = 'application/json';
		options.body = JSON.stringify(body);
	}
	return fetch(url, options);
}

/**
 * Perform an unauthenticated POST request with JSON body.
 * Used for login, register, and other public auth endpoints.
 */
function publicPost(url: string, body: unknown): Promise<Response> {
	return fetch(url, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(body),
		signal: AbortSignal.timeout(30_000)
	});
}

/**
 * Build a query string from an object of key/value pairs.
 * Omits keys whose values are `undefined` or `null`.
 */
function qs(params: Record<string, string | number | boolean | undefined | null>): string {
	const sp = new URLSearchParams();
	for (const [k, v] of Object.entries(params)) {
		if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
	}
	const str = sp.toString();
	return str ? `?${str}` : '';
}

// ═══════════════════════════════════════════════════════════════
//  PUBLIC / CONFIGURATION
// ═══════════════════════════════════════════════════════════════

/** Fetch application name + version. No auth required. */
export async function getConfiguration(): Promise<Configuration> {
	const res = await fetch(`${base}${api.configuration}`);
	return unwrap<Configuration>(res);
}

// ═══════════════════════════════════════════════════════════════
//  AUTH — UNAUTHENTICATED (rate-limited)
// ═══════════════════════════════════════════════════════════════

/** Password login. Returns access token or 2FA challenge. */
export async function login(email: string, password: string): Promise<AuthTokenResponse> {
	const res = await publicPost(`${base}${api.login}`, { email, password });
	return unwrap<AuthTokenResponse>(res);
}

/** Register a new user account. Returns the created user (no token — user must verify email first). */
export async function register(data: {
	firstName: string;
	lastName: string;
	email: string;
	password: string;
}): Promise<{ user: User }> {
	const res = await publicPost(`${base}${api.register}`, data);
	return unwrap<{ user: User }>(res);
}

/** Google OAuth authorization-code login. */
export async function loginGoogle(code: string): Promise<AuthTokenResponse> {
	const res = await publicPost(`${base}${api.loginGoogle}`, { code });
	return unwrap<AuthTokenResponse>(res);
}

/**
 * Sign in with Apple authorization-code login.
 *
 * Apple discloses the user's name only in the browser response to their very
 * first authorisation, so it travels alongside the code rather than inside the
 * identity token.
 *
 * @param code - Authorization code from `AppleID.auth.signIn()`.
 * @param name - First/last name Apple disclosed, when it did.
 */
export async function loginApple(
	code: string,
	name?: { firstName?: string; lastName?: string }
): Promise<AuthTokenResponse> {
	const res = await publicPost(`${base}${api.loginApple}`, {
		code,
		firstName: name?.firstName,
		lastName: name?.lastName
	});
	return unwrap<AuthTokenResponse>(res);
}

/** Sign in with Microsoft authorization-code login. */
export async function loginMicrosoft(code: string): Promise<AuthTokenResponse> {
	const res = await publicPost(`${base}${api.loginMicrosoft}`, { code });
	return unwrap<AuthTokenResponse>(res);
}

/**
 * Request a sign-in code for an email — the login page's "Continue" call.
 *
 * The server returns the method the user should sign in with. When it is
 * `'email'` an OTP was sent; when it is `'password'` or `'passkey'` no code
 * was sent and the login page switches to that flow instead.
 *
 * @param email - The email address to sign in with.
 * @param force - When `true`, always send an OTP regardless of the account's
 *   preferred method (used by "Sign in with email code instead" / "Resend").
 */
export async function requestEmailOTP(
	email: string,
	force: boolean = false
): Promise<{ method: 'email' | 'password' | 'passkey' }> {
	const res = await publicPost(`${base}${api.emailOtpRequest}`, { email, force });
	return unwrap<{ method: 'email' | 'password' | 'passkey' }>(res);
}

/** Verify an email OTP code and receive a token. */
export async function verifyEmailOTP(email: string, code: string): Promise<AuthTokenResponse> {
	const res = await publicPost(`${base}${api.emailOtpVerify}`, { email, code });
	return unwrap<AuthTokenResponse>(res);
}

/** Verify 2FA TOTP code during login (when `requiresTwoFactor` was true). */
export async function verifyTwoFactor(twoFactorToken: string, code: string): Promise<AuthTokenResponse> {
	const res = await publicPost(`${base}${api.twoFactorVerify}`, { twoFactorToken, code });
	return unwrap<AuthTokenResponse>(res);
}

/** Initiate a password-reset flow (sends email). */
export async function forgotPassword(email: string): Promise<void> {
	const res = await publicPost(`${base}${api.forgotPassword}`, { email });
	await unwrap<unknown>(res);
}

/** Validate that a password-reset verification code is still valid. */
export async function validateResetCode(verificationCode: string): Promise<void> {
	const res = await publicPost(`${base}${api.resetPasswordValidate}`, { verificationCode });
	await unwrap<unknown>(res);
}

/** Complete password reset — set new password. */
export async function resetPassword(verificationCode: string, newPassword: string): Promise<void> {
	const res = await publicPost(`${base}${api.resetPassword}`, { verificationCode, newPassword });
	await unwrap<unknown>(res);
}

/** Verify a new account's email address. */
export async function verifyAccount(userId: number, secretKey: string): Promise<void> {
	const res = await publicPost(`${base}${api.verifyAccount}`, { userId, secretKey });
	await unwrap<unknown>(res);
}

// ═══════════════════════════════════════════════════════════════
//  AUTH — AUTHENTICATED
// ═══════════════════════════════════════════════════════════════

/** Refresh the current JWT. Returns new access token with fresh context. */
export async function refreshToken(): Promise<AuthTokenResponse> {
	const res = await authPost(`${base}${api.refresh}`);
	return unwrap<AuthTokenResponse>(res);
}

/** Get the current authenticated user (from token). */
export async function getAuthMe(): Promise<User> {
	const res = await authFetch(`${base}${api.authMe}`);
	const data = await unwrap<{ user: User }>(res);
	return data.user;
}

/** Begin 2FA setup — returns secret + otpauth URI for QR code generation. */
export async function setupTwoFactor(): Promise<{ secret: string; uri: string }> {
	const res = await authPost(`${base}${api.twoFactorSetup}`);
	return unwrap<{ secret: string; uri: string }>(res);
}

/** Confirm 2FA setup by verifying a TOTP code. Returns backup codes. */
export async function confirmTwoFactor(code: string): Promise<{ backupCodes: string[] }> {
	const res = await authPost(`${base}${api.twoFactorConfirm}`, { code });
	return unwrap<{ backupCodes: string[] }>(res);
}

/** Disable 2FA (requires a valid TOTP or backup code). */
export async function disableTwoFactor(code: string): Promise<void> {
	const res = await authDelete(`${base}${api.twoFactorDisable}`, { code });
	await unwrap<unknown>(res);
}

// ═══════════════════════════════════════════════════════════════
//  APPLICATION CONSENT FLOW
// ═══════════════════════════════════════════════════════════════

/** Fetch public (non-sensitive) info about an application by UUID. No auth required. */
export async function getApplicationPublicInfo(uuid: string): Promise<ApplicationPublicInfo> {
	const res = await fetch(`${base}${api.applicationPublic}/${uuid}`);
	return unwrap<ApplicationPublicInfo>(res);
}

/**
 * Authorize the current user for an application (consent flow).
 * Creates or returns the UserApplication association.
 */
export async function authorizeForApplication(appUUID: string, returnURL?: string): Promise<AuthorizeResult> {
	const res = await authPost(`${base}${api.authorize}`, { appUUID, returnURL });
	return unwrap<AuthorizeResult>(res);
}

/**
 * Check if the current user is already authorized for an application.
 * Used to skip the consent screen for returning users.
 */
export async function checkApplicationAuthorization(
	appUUID: string
): Promise<{ isAuthorized: boolean; applicationName: string }> {
	const res = await authFetch(`${base}/api/auth/check-authorization/${encodeURIComponent(appUUID)}`);
	return unwrap<{ isAuthorized: boolean; applicationName: string }>(res);
}

/**
 * Create an OAuth 2.0 authorization code after user consent.
 * Used by the Authorize page when the flow is an OAuth redirect.
 */
export async function createOAuthAuthorizationCode(params: {
	client_id: string;
	redirect_uri: string;
	scope: string;
	code_challenge: string;
	code_challenge_method: string;
	state?: string;
	nonce?: string;
}): Promise<OAuthAuthorizeResult> {
	const res = await authPost(`${base}${api.oauthAuthorize}`, params);
	return unwrap<OAuthAuthorizeResult>(res);
}

/** Revoke the current JWT (server-side logout).
 *
 * Deliberately bypasses the global `unwrap()` 401 force-logout: a 401 here
 * simply means the token was already invalid server-side, which is the
 * post-logout state we want. Letting `unwrap()` fire would hard-redirect
 * to bare `/login` and clobber callers that hold an authorize/returnURL
 * context (see Authorize.svelte). Non-2xx responses other than 401 are
 * surfaced so the caller can decide. */
export async function serverLogout(): Promise<void> {
	const res = await authPost(`${base}${api.logout}`, {});
	if (res.ok || res.status === 401) return;
	throw new Error(`Logout failed (${res.status})`);
}

/** Revoke all active tokens for the current user. */
export async function logoutAll(): Promise<void> {
	const res = await authPost(`${base}${api.logoutAll}`, {});
	await unwrap<unknown>(res);
}

/** Revoke all sessions for a user (admin). */
export async function revokeUserSessions(userId: number): Promise<void> {
	const res = await authDelete(`${base}${api.userSessions}/${userId}/sessions`);
	await unwrap<unknown>(res);
}

// ═══════════════════════════════════════════════════════════════
//  USER (admin + legacy compat)
// ═══════════════════════════════════════════════════════════════

/** Get the current user via legacy endpoint. */
export async function getCurrentUser(): Promise<User> {
	const res = await authFetch(`${base}${api.userMe}`);
	return unwrap<User>(res);
}

/** List users with optional search and filters (admin). */
export async function listUsers(
	params: {
		limit?: number;
		offset?: number;
		search?: string;
		roleId?: number;
		dateFrom?: string;
		dateTo?: string;
		passwordless?: boolean;
		sortBy?: string;
		sortDir?: SortDirection;
	} = {}
): Promise<PaginatedResponse<User>> {
	const url = `${base}${api.userList}${qs(params)}`;
	return unwrapPaginated<User>(await authFetch(url));
}

/** Admin-create a new user account. */
export async function adminCreateUser(data: {
	email: string;
	password?: string;
	firstName?: string;
	lastName?: string;
	username?: string;
	isActive?: boolean;
	isVerified?: boolean;
}): Promise<User> {
	const res = await authPost(`${base}${api.userCreate}`, data);
	return unwrap<User>(res);
}

/** Get a single user by ID (admin). */
export async function getUser(id: number): Promise<User> {
	const res = await authFetch(`${base}${api.user}/${id}`);
	return unwrap<User>(res);
}

/** Update a user (admin). */
export async function updateUser(
	id: number,
	data: Partial<
		Pick<
			User,
			| 'isAdmin'
			| 'isActive'
			| 'isVerified'
			| 'firstName'
			| 'lastName'
			| 'username'
			| 'about'
			| 'company'
			| 'website'
			| 'location'
			| 'notes'
		>
	>
): Promise<User> {
	const res = await authPut(`${base}${api.user}/${id}`, data);
	return unwrap<User>(res);
}

/** Soft-delete a user (admin). Sets the deleted timestamp; user is excluded from queries. */
export async function deleteUser(id: number): Promise<void> {
	const res = await authDelete(`${base}${api.user}/${id}`);
	await unwrap<unknown>(res);
}

/** Permanently delete a user and all associated data (admin). Irreversible. */
export async function hardDeleteUser(id: number): Promise<void> {
	const res = await authDelete(`${base}${api.user}/${id}/permanent`);
	await unwrap<unknown>(res);
}

/** Bulk-import dormant users from a CSV parse result (admin). */
export async function importUsers(
	rows: Array<{ email: string; firstName?: string; lastName?: string }>,
	applicationIds: number[]
): Promise<ImportUsersResult> {
	const res = await authPost(`${base}${api.userImport}`, { rows, applicationIds });
	return unwrap<ImportUsersResult>(res);
}

/** Preview an import — returns which emails already exist in the database (admin). */
export async function previewImport(emails: string[]): Promise<{ existing: string[] }> {
	const res = await authPost(`${base}${api.userImportPreview}`, { emails });
	return unwrap<{ existing: string[] }>(res);
}

/** What a token export would contain, before one is built. */
export interface TokenExportSummary {
	userCount: number;
	transactionCount: number;
	totalBalance: number;
}

/** Summarise what a token export would contain, without building it (admin). */
export async function getTokenExportSummary(): Promise<TokenExportSummary> {
	const res = await authFetch(`${base}${api.tokenExportSummary}`);
	return unwrap<TokenExportSummary>(res);
}

/**
 * Downloads the token export as a JSON file.
 *
 * Fetched with the Authorization header and handed to the browser as a blob, rather than pointed at
 * with `window.open`. The endpoint is admin-only and authenticated by header, so a plain navigation
 * would arrive unauthenticated; putting the token in the query string instead would leak a
 * credential into browser history and every proxy log between here and the server.
 *
 * @param options.includeTransactions Whether to include the ledger behind each balance.
 * @param options.since               Only include ledger entries at or after this date.
 * @return The filename the server chose, so the caller can report what was saved.
 */
export async function downloadTokenExport(
	options: { includeTransactions?: boolean; since?: string } = {}
): Promise<{ filename: string; sizeInBytes: number }> {
	const parameters = new URLSearchParams();
	/* Only sent when turning the ledger off — the server includes it by default. */
	if (options.includeTransactions === false) parameters.set('transactions', 'false');
	/* Omitted entirely when unset, so an empty date field does not become a floor of "no date". */
	if (options.since) parameters.set('since', options.since);

	const token = getToken();
	/* Abort early rather than issuing a request that can only come back 401. */
	if (!token) throw new Error('Not authenticated');

	const response = await fetch(`${base}${api.tokenExport}?${parameters.toString()}`, {
		headers: { Authorization: `Bearer ${token}` },
		/* No timeout: a full ledger export on a mature deployment legitimately takes minutes, and
		   the 30s default the other helpers use would abort it partway through. */
		signal: undefined
	});

	/* Checked before touching the body: an error response is JSON too, and saving it as the export
	   would hand the operator a file that looks like a download and imports as nothing. */
	if (!response.ok) {
		throw new Error(`Export failed with status ${response.status}.`);
	}

	/* Prefer the server's filename — it is dated, and an operator will refer to it later. */
	const disposition = response.headers.get('Content-Disposition') ?? '';
	const match = /filename="([^"]+)"/.exec(disposition);
	const filename = match?.[1] ?? 'abstractid-tokens.json';

	const blob = await response.blob();
	saveBlob(blob, filename);

	return { filename, sizeInBytes: blob.size };
}

/** Get applications a user is registered to (admin). */
export async function getUserApplications(userId: number): Promise<UserApplication[]> {
	const res = await authFetch(`${base}${api.userApplications}/${userId}/applications`);
	return unwrap<UserApplication[]>(res);
}

/** List notifications for a specific user (admin). */
export async function listUserNotifications(
	userId: number,
	params: { limit?: number; offset?: number } = {}
): Promise<PaginatedResponse<UserNotification>> {
	const url = `${base}${api.userNotifications}/${userId}/notifications${qs(params)}`;
	return unwrapPaginated<UserNotification>(await authFetch(url));
}

/** Create a notification for a specific user (admin). */
export async function createUserNotification(
	userId: number,
	data: { title: string; message?: string; notificationType?: string; actionUrl?: string }
): Promise<UserNotification> {
	const res = await authPost(`${base}${api.userNotifications}/${userId}/notifications`, data);
	return unwrap<UserNotification>(res);
}

// ═══════════════════════════════════════════════════════════════
//  AUDIT (admin)
// ═══════════════════════════════════════════════════════════════

/** List audit log entries with optional filters (admin). Returns a `users` map resolving IDs to names. */
export async function listAuditLogs(
	params: {
		limit?: number;
		offset?: number;
		action?: string;
		userId?: number;
		relatedUserId?: number;
		dateFrom?: string;
		dateTo?: string;
		sortBy?: string;
		sortDir?: SortDirection;
	} = {}
): Promise<AuditListResponse> {
	const url = `${base}${api.auditList}${qs(params)}`;
	const res = await authFetch(url);
	return unwrap<AuditListResponse>(res);
}

// ═══════════════════════════════════════════════════════════════
//  PROFILE (authenticated)
// ═══════════════════════════════════════════════════════════════

/** Get the current user's full profile. */
export async function getProfile(): Promise<User> {
	const res = await authFetch(`${base}${api.profile}`);
	return unwrap<User>(res);
}

/** Update the current user's profile fields. */
export async function updateProfile(
	data: Partial<
		Pick<
			User,
			| 'firstName'
			| 'lastName'
			| 'about'
			| 'company'
			| 'website'
			| 'location'
			| 'username'
			| 'themeMode'
			| 'languageSettingsMode'
			| 'preferredAuthMethod'
			| 'newsletterOptOut'
		>
	>
): Promise<User> {
	const res = await authPut(`${base}${api.profile}`, data);
	return unwrap<User>(res);
}

/**
 * Persist (or forget) the user's preferred sign-in method after a
 * successful login, honouring the "Remember my choice" checkbox.
 *
 * Writes to both the device-local store and the account, and only when
 * the value actually changes — avoiding a redundant network write on
 * every login. Account writes are best-effort: a failure leaves the
 * device-local store as the source of truth. Pass no method (e.g. Google
 * sign-in, which is not a rememberable choice) to skip entirely.
 *
 * Shared by the login and two-factor pages so the preference is saved
 * regardless of whether a 2FA step was required.
 *
 * @param method   - The method just used, or `undefined` to skip.
 * @param remember - Whether the user opted to remember the choice.
 */
export async function savePreferredAuthMethod(
	method: 'email' | 'password' | 'passkey' | undefined,
	remember: boolean
): Promise<void> {
	/* Nothing to remember for methods outside the chooser (e.g. Google). */
	if (!method) return;

	const saved = get(preferredAuthMethod);

	/* Save when remembering, clear when not — both only on change. */
	if (remember) {
		/* Skip the network write if the account already has this method. */
		if (saved !== method) {
			preferredAuthMethod.set(method);
			try {
				await updateProfile({ preferredAuthMethod: method });
			} catch {
				/* Best-effort: the device-local store already holds it. */
			}
		}
	} else if (saved) {
		/* Box unticked but a preference exists — forget it everywhere. */
		preferredAuthMethod.set('');
		try {
			await updateProfile({ preferredAuthMethod: null });
		} catch {
			/* Best-effort: the device-local store is already cleared. */
		}
	}
}

/**
 * Upload a new avatar image for the current user.
 * The server resizes/crops to 256×256 PNG.
 */
export async function uploadAvatar(file: Blob): Promise<User> {
	const token = getToken();
	/** Abort early if there is no stored JWT. */
	if (!token) throw new Error('Not authenticated');
	const form = new FormData();
	form.append('avatar', file, 'avatar.png');
	const res = await fetch(`${base}${api.profileAvatar}`, {
		method: 'POST',
		headers: { Authorization: `Bearer ${token}` },
		body: form,
		signal: AbortSignal.timeout(30_000)
	});
	return unwrap<User>(res);
}

/** Delete the current user's avatar. */
export async function deleteAvatar(): Promise<User> {
	const res = await authDelete(`${base}${api.profileAvatar}`);
	return unwrap<User>(res);
}

/** Change the current user's password (or set one for passwordless accounts). */
export async function changePassword(currentPassword: string | null, newPassword: string): Promise<void> {
	const body: Record<string, string> = { newPassword };
	if (currentPassword) body.currentPassword = currentPassword;
	const res = await authPut(`${base}${api.profilePassword}`, body);
	await unwrap<unknown>(res);
}

/** Request an email change — sends OTP to the new address. */
export async function requestEmailChangeOTP(newEmail: string): Promise<void> {
	const res = await authPost(`${base}${api.profileEmailRequest}`, { newEmail });
	await unwrap<unknown>(res);
}

/** Confirm email change with the 6-digit OTP. Returns the new email. */
export async function confirmEmailChange(code: string): Promise<{ email: string }> {
	const res = await authPost(`${base}${api.profileEmailConfirm}`, { code });
	return unwrap<{ email: string; message: string }>(res);
}

/** Check whether a username is valid and available. */
export async function checkUsernameAvailability(username: string): Promise<{ valid: boolean; available: boolean; message: string }> {
	const res = await authFetch(`${base}${api.profileUsernameCheck}?username=${encodeURIComponent(username)}`);
	return unwrap<{ valid: boolean; available: boolean; message: string }>(res);
}

/** Request a username change — sends OTP to the user's current email. */
export async function requestUsernameChangeOTP(newUsername: string): Promise<void> {
	const res = await authPost(`${base}${api.profileUsernameRequest}`, { newUsername });
	await unwrap<unknown>(res);
}

/** Confirm username change with the 6-digit OTP. Returns the new username. */
export async function confirmUsernameChange(code: string): Promise<{ username: string }> {
	const res = await authPost(`${base}${api.profileUsernameConfirm}`, { code });
	return unwrap<{ username: string; message: string }>(res);
}

/** Request deletion of the current user's account — sends OTP to their current email. */
export async function requestAccountDeletionOTP(): Promise<void> {
	const res = await authPost(`${base}${api.profileDeletionRequest}`, {});
	await unwrap<unknown>(res);
}

/** Confirm account deletion with the 6-digit OTP. The account is soft-deleted server-side. */
export async function confirmAccountDeletion(code: string): Promise<void> {
	const res = await authPost(`${base}${api.profileDeletionConfirm}`, { code });
	await unwrap<unknown>(res);
}

/** List the current user's notifications. */
export async function listNotifications(
	params: { limit?: number; offset?: number } = {}
): Promise<PaginatedResponse<UserNotification>> {
	const url = `${base}${api.profileNotifications}${qs(params)}`;
	return unwrapPaginated<UserNotification>(await authFetch(url));
}

/** Mark all notifications as read. */
export async function markAllNotificationsRead(): Promise<void> {
	const res = await authPut(`${base}${api.profileNotificationsReadAll}`);
	await unwrap<unknown>(res);
}

/** Mark a single notification as read. */
export async function markNotificationRead(id: number): Promise<void> {
	const res = await authPut(`${base}${api.profileNotificationRead}/${id}/read`);
	await unwrap<unknown>(res);
}

/** Delete a single notification. */
export async function deleteNotification(id: number): Promise<void> {
	const res = await authDelete(`${base}${api.profileNotificationDelete}/${id}`);
	await unwrap<unknown>(res);
}

/** List applications the current user is registered with. */
export async function listMyApplications(): Promise<UserApplication[]> {
	const res = await authFetch(`${base}${api.profileApplications}`);
	return unwrap<UserApplication[]>(res);
}

// ═══════════════════════════════════════════════════════════════
//  SESSIONS (authenticated)
// ═══════════════════════════════════════════════════════════════

/** List the current user's active sessions. */
export async function listSessions(): Promise<Session[]> {
	const res = await authFetch(`${base}${api.profileSessions}`);
	return unwrap<Session[]>(res);
}

/** Revoke a specific session by ID. */
export async function revokeSession(id: number): Promise<void> {
	const res = await authDelete(`${base}${api.profileSession}/${id}`);
	await unwrap<unknown>(res);
}

/** Revoke all sessions except the current one. */
export async function revokeOtherSessions(): Promise<{ revoked: number }> {
	const res = await authDelete(`${base}${api.profileSessionsRevokeOthers}`);
	return unwrap<{ revoked: number }>(res);
}

// ═══════════════════════════════════════════════════════════════
//  PASSKEYS (authenticated — management)
// ═══════════════════════════════════════════════════════════════

/** List the current user's registered passkeys. */
export async function listPasskeys(): Promise<Passkey[]> {
	const res = await authFetch(`${base}${api.profilePasskeys}`);
	return unwrap<Passkey[]>(res);
}

/** Start passkey registration — returns WebAuthn options + challenge key. */
export async function passkeyRegisterOptions(): Promise<{
	options: PublicKeyCredentialCreationOptions;
	challenge: string;
}> {
	const res = await authPost(`${base}${api.profilePasskeyRegisterOptions}`);
	return unwrap<{ options: PublicKeyCredentialCreationOptions; challenge: string }>(res);
}

/** Complete passkey registration by sending the attestation response. */
export async function passkeyRegisterVerify(
	challengeKey: string,
	response: unknown,
	friendlyName?: string
): Promise<Passkey> {
	const res = await authPost(`${base}${api.profilePasskeyRegisterVerify}`, { challengeKey, response, friendlyName });
	return unwrap<Passkey>(res);
}

/** Rename a passkey. */
export async function updatePasskeyName(id: number, friendlyName: string): Promise<Passkey> {
	const res = await authPut(`${base}${api.profilePasskey}/${id}`, { friendlyName });
	return unwrap<Passkey>(res);
}

/** Delete a passkey. */
export async function deletePasskey(id: number): Promise<void> {
	const res = await authDelete(`${base}${api.profilePasskeyDelete}/${id}`);
	await unwrap<unknown>(res);
}

// ═══════════════════════════════════════════════════════════════
//  PASSKEYS (unauthenticated — authentication)
// ═══════════════════════════════════════════════════════════════

/** Request passkey authentication options (unauthenticated). */
export async function passkeyAuthOptions(
	email?: string
): Promise<{ options: PublicKeyCredentialRequestOptions; challenge: string }> {
	const res = await publicPost(`${base}${api.passkeyAuthOptions}`, { email });
	return unwrap<{ options: PublicKeyCredentialRequestOptions; challenge: string }>(res);
}

/** Verify passkey authentication and receive a token (unauthenticated). */
export async function passkeyAuthVerify(challengeKey: string, response: unknown): Promise<AuthTokenResponse> {
	const res = await publicPost(`${base}${api.passkeyAuthVerify}`, { challengeKey, response });
	return unwrap<AuthTokenResponse>(res);
}

// ═══════════════════════════════════════════════════════════════
//  APPLICATIONS (admin)
// ═══════════════════════════════════════════════════════════════

/** List all applications (admin). Server returns paginated; we extract items. */
export async function listApplications(
	params: { limit?: number; offset?: number; sortBy?: string; sortDir?: SortDirection } = {}
): Promise<Application[]> {
	const res = await authFetch(`${base}${api.applications}${qs(params)}`);
	const data = await unwrapPaginated<Application>(res);
	return data.items;
}

/** Get a single application by ID (admin). */
export async function getApplication(id: number): Promise<Application> {
	const res = await authFetch(`${base}${api.application}/${id}`);
	return unwrap<Application>(res);
}

/** Create a new application (admin). */
export async function createApplication(data: Partial<Application>): Promise<Application> {
	const res = await authPost(`${base}${api.applications}`, data);
	return unwrap<Application>(res);
}

/** Update an application (admin). */
export async function updateApplication(id: number, data: Partial<Application>): Promise<Application> {
	const res = await authPut(`${base}${api.application}/${id}`, data);
	return unwrap<Application>(res);
}

/** Delete an application (admin). */
export async function deleteApplication(id: number): Promise<void> {
	const res = await authDelete(`${base}${api.application}/${id}`);
	await unwrap<unknown>(res);
}

/** Register a user to an application (admin). */
export async function registerUserToApplication(appId: number, userId: number): Promise<void> {
	const res = await authPost(`${base}${api.applicationRegisterUser}/${appId}/users/${userId}`);
	await unwrap<unknown>(res);
}

/** Set a user's permission on an application (admin). */
export async function setApplicationPermission(
	appId: number,
	userId: number,
	isUserPermissionGranted: boolean
): Promise<void> {
	const res = await authPut(`${base}${api.applicationSetPermission}/${appId}/users/${userId}/permission`, {
		isUserPermissionGranted
	});
	await unwrap<unknown>(res);
}

/** Unregister a user from an application (admin). */
export async function unregisterUserFromApplication(appId: number, userId: number): Promise<void> {
	const res = await authDelete(`${base}${api.applicationUnregisterUser}/${appId}/users/${userId}`);
	await unwrap<unknown>(res);
}

/** Upload a logo image for an application (admin). */
export async function uploadApplicationLogo(appId: number, file: File): Promise<Application> {
	const fd = new FormData();
	fd.append('logo', file);
	const res = await authUpload(`${base}${api.applicationUploadLogo}/${appId}/logo`, fd);
	return unwrap<Application>(res);
}

/** Delete the logo of an application (admin). */
export async function deleteApplicationLogo(appId: number): Promise<Application> {
	const res = await authDelete(`${base}${api.applicationDeleteLogo}/${appId}/logo`);
	return unwrap<Application>(res);
}

/** Upload an icon image for an application (admin). */
export async function uploadApplicationIcon(appId: number, file: File): Promise<Application> {
	const fd = new FormData();
	fd.append('icon', file);
	const res = await authUpload(`${base}${api.applicationUploadIcon}/${appId}/icon`, fd);
	return unwrap<Application>(res);
}

/** Delete the icon of an application (admin). */
export async function deleteApplicationIcon(appId: number): Promise<Application> {
	const res = await authDelete(`${base}${api.applicationDeleteIcon}/${appId}/icon`);
	return unwrap<Application>(res);
}

// ═══════════════════════════════════════════════════════════════
//  ROLES (admin)
// ═══════════════════════════════════════════════════════════════

/** List all roles (admin). Server returns paginated; we extract items. */
export async function listRoles(params: { sortBy?: string; sortDir?: SortDirection } = {}): Promise<Role[]> {
	const res = await authFetch(`${base}${api.roles}${qs(params)}`);
	const data = await unwrapPaginated<Role>(res);
	return data.items;
}

/** Get a single role by ID (admin). */
export async function getRole(id: number): Promise<Role> {
	const res = await authFetch(`${base}${api.role}/${id}`);
	return unwrap<Role>(res);
}

/** Create a new role (admin). */
export async function createRole(data: Partial<Role>): Promise<Role> {
	const res = await authPost(`${base}${api.roles}`, data);
	return unwrap<Role>(res);
}

/** Update a role (admin). */
export async function updateRole(id: number, data: Partial<Role>): Promise<Role> {
	const res = await authPut(`${base}${api.role}/${id}`, data);
	return unwrap<Role>(res);
}

/** Delete a role (admin). */
export async function deleteRole(id: number): Promise<void> {
	const res = await authDelete(`${base}${api.role}/${id}`);
	await unwrap<unknown>(res);
}

/** Get roles assigned to a specific user (admin). */
export async function getUserRoles(userId: number): Promise<RoleReference[]> {
	const res = await authFetch(`${base}${api.rolesForUser}/${userId}`);
	return unwrap<RoleReference[]>(res);
}

/** Set all roles for a user at once (admin). Replaces existing assignments. */
export async function setUserRoles(userId: number, roleIds: number[]): Promise<void> {
	const res = await authPut(`${base}${api.setUserRoles}/${userId}`, { roleIds });
	await unwrap<unknown>(res);
}

/** Assign a single role to a user (admin). */
export async function assignRole(roleId: number, userId: number): Promise<void> {
	const res = await authPost(`${base}${api.roleAssign}/${roleId}/assign/${userId}`);
	await unwrap<unknown>(res);
}

/** Remove a single role from a user (admin). */
export async function removeRole(roleId: number, userId: number): Promise<void> {
	const res = await authDelete(`${base}${api.roleRemove}/${roleId}/assign/${userId}`);
	await unwrap<unknown>(res);
}

// ═══════════════════════════════════════════════════════════════
//  SETTINGS (admin)
// ═══════════════════════════════════════════════════════════════

/** Get global platform settings (admin). */
export async function getSettings(): Promise<Settings> {
	const res = await authFetch(`${base}${api.settings}`);
	return unwrap<Settings>(res);
}

/** Update global platform settings (admin). */
export async function updateSettings(data: Partial<Settings>): Promise<Settings> {
	const res = await authPut(`${base}${api.settings}`, data);
	return unwrap<Settings>(res);
}

/** Update SMTP configuration (admin). */
export async function updateSMTP(data: SMTPConfig): Promise<Settings> {
	const res = await authPut(`${base}${api.settingsSMTP}`, data);
	return unwrap<Settings>(res);
}

/** Send a test email via current SMTP config (admin). */
export async function testSMTP(to: string): Promise<void> {
	const res = await authPost(`${base}${api.settingsSMTPTest}`, { email: to });
	await unwrap<unknown>(res);
}

/** Verify SMTP connection (admin). */
export async function verifySMTP(): Promise<void> {
	const res = await authPost(`${base}${api.settingsSMTPVerify}`, {});
	await unwrap<unknown>(res);
}

/** Upload an icon image for a sidebar link by index (admin). */
export async function uploadSidebarLinkIcon(index: number, file: File): Promise<Settings> {
	const fd = new FormData();
	fd.append('icon', file);
	const res = await authUpload(`${base}${api.settingsSidebarLinkIcon}/${index}/icon`, fd);
	return unwrap<Settings>(res);
}

/** Remove the icon image from a sidebar link by index (admin). */
export async function deleteSidebarLinkIcon(index: number): Promise<Settings> {
	const res = await authDelete(`${base}${api.settingsSidebarLinkIcon}/${index}/icon`);
	return unwrap<Settings>(res);
}

// ═══════════════════════════════════════════════════════════════
//  EMAIL TEMPLATES (admin)
// ═══════════════════════════════════════════════════════════════

/** List all email templates (admin). Server returns paginated; we extract items. */
export async function listEmailTemplates(
	params: { sortBy?: string; sortDir?: SortDirection } = {}
): Promise<EmailTemplate[]> {
	const res = await authFetch(`${base}${api.emailTemplates}${qs(params)}`);
	const data = await unwrapPaginated<EmailTemplate>(res);
	return data.items;
}

/** Get a single email template by ID (admin). */
export async function getEmailTemplate(id: number): Promise<EmailTemplate> {
	const res = await authFetch(`${base}${api.emailTemplate}/${id}`);
	return unwrap<EmailTemplate>(res);
}

/** Create a new email template (admin). */
export async function createEmailTemplate(data: Partial<EmailTemplate>): Promise<EmailTemplate> {
	const res = await authPost(`${base}${api.emailTemplates}`, data);
	return unwrap<EmailTemplate>(res);
}

/** Update an email template (admin). */
export async function updateEmailTemplate(id: number, data: Partial<EmailTemplate>): Promise<EmailTemplate> {
	const res = await authPut(`${base}${api.emailTemplate}/${id}`, data);
	return unwrap<EmailTemplate>(res);
}

/** Delete an email template (admin). */
export async function deleteEmailTemplate(id: number): Promise<void> {
	const res = await authDelete(`${base}${api.emailTemplate}/${id}`);
	await unwrap<unknown>(res);
}

/** Preview a rendered email template (admin). */
export async function previewEmailTemplate(
	id: number,
	variables?: Record<string, string>,
	masterName?: string
): Promise<{ html: string; text: string }> {
	const res = await authPost(`${base}${api.emailTemplatePreview}/${id}/preview`, { variables, masterName });
	return unwrap<{ html: string; text: string }>(res);
}

/** List placeholders for a template (admin). */
export async function listPlaceholders(templateId: number): Promise<Placeholder[]> {
	const res = await authFetch(`${base}${api.emailTemplatePlaceholders}/${templateId}/placeholders`);
	return unwrap<Placeholder[]>(res);
}

/** Create or update a placeholder on a template (admin). */
export async function upsertPlaceholder(templateId: number, data: Partial<Placeholder>): Promise<Placeholder> {
	const res = await authPost(`${base}${api.emailTemplatePlaceholders}/${templateId}/placeholders`, data);
	return unwrap<Placeholder>(res);
}

/** Delete a placeholder by its own ID (admin). */
export async function deletePlaceholder(phId: number): Promise<void> {
	const res = await authDelete(`${base}${api.emailTemplatePlaceholderDelete}/${phId}`);
	await unwrap<unknown>(res);
}

// ── Newsletter Management ─────────────────────────────────────

/** List newsletter campaigns (admin). */
export async function listNewsletters(params?: {
	limit?: number;
	offset?: number;
	status?: string;
	search?: string;
	sortBy?: string;
	sortDir?: SortDirection;
}): Promise<PaginatedResponse<Newsletter>> {
	const res = await authFetch(`${base}${api.newsletters}${qs(params ?? {})}`);
	return unwrapPaginated<Newsletter>(res);
}

/** Get a single newsletter by ID (admin). */
export async function getNewsletter(id: number): Promise<Newsletter> {
	const res = await authFetch(`${base}${api.newsletter}/${id}`);
	return unwrap<Newsletter>(res);
}

/** Create a new newsletter campaign (admin). */
export async function createNewsletter(data: Partial<Newsletter>): Promise<Newsletter> {
	const res = await authPost(`${base}${api.newsletters}`, data);
	return unwrap<Newsletter>(res);
}

/** Update a newsletter campaign (admin). */
export async function updateNewsletter(id: number, data: Partial<Newsletter>): Promise<Newsletter> {
	const res = await authPut(`${base}${api.newsletter}/${id}`, data);
	return unwrap<Newsletter>(res);
}

/** Delete a newsletter campaign (admin). */
export async function deleteNewsletter(id: number): Promise<void> {
	const res = await authDelete(`${base}${api.newsletter}/${id}`);
	await unwrap<unknown>(res);
}

/** Initiate dispatch of a newsletter campaign (admin). */
export async function sendNewsletter(id: number): Promise<Newsletter> {
	const res = await authPost(`${base}${api.newsletterSend}/${id}/send`);
	return unwrap<Newsletter>(res);
}

/** Fetch pre-send staging data for a newsletter (admin). */
export async function getNewsletterStaging(id: number): Promise<NewsletterStagingData> {
	const res = await authFetch(`${base}${api.newsletterStaging}/${id}/staging`);
	return unwrap<NewsletterStagingData>(res);
}

/** Pause a sending newsletter (admin). */
export async function pauseNewsletter(id: number): Promise<Newsletter> {
	const res = await authPost(`${base}${api.newsletterPause}/${id}/pause`);
	return unwrap<Newsletter>(res);
}

/** Resume a paused newsletter (admin). */
export async function resumeNewsletter(id: number): Promise<Newsletter> {
	const res = await authPost(`${base}${api.newsletterResume}/${id}/resume`);
	return unwrap<Newsletter>(res);
}

/** Cancel a sending or paused newsletter (admin). */
export async function cancelNewsletter(id: number): Promise<Newsletter> {
	const res = await authPost(`${base}${api.newsletterCancel}/${id}/cancel`);
	return unwrap<Newsletter>(res);
}

/** Get analytics for a newsletter campaign (admin). */
export async function getNewsletterAnalytics(id: number): Promise<NewsletterAnalytics> {
	const res = await authFetch(`${base}${api.newsletterAnalytics}/${id}/analytics`);
	return unwrap<NewsletterAnalytics>(res);
}

/** List recipients for a newsletter campaign (admin). */
export async function listNewsletterRecipients(
	id: number,
	params?: { limit?: number; offset?: number; status?: string; sortBy?: string; sortDir?: SortDirection }
): Promise<PaginatedResponse<NewsletterRecipient>> {
	const res = await authFetch(`${base}${api.newsletterRecipients}/${id}/recipients${qs(params ?? {})}`);
	return unwrapPaginated<NewsletterRecipient>(res);
}

/** Render a newsletter preview with mock data (admin). */
export async function previewNewsletter(
	id: number,
	masterTemplateId?: number
): Promise<{ subject: string; html: string; text: string | null }> {
	const body: Record<string, unknown> = {};
	if (masterTemplateId) body.masterTemplateId = masterTemplateId;
	const res = await authPost(`${base}${api.newsletterPreview}/${id}/preview`, body);
	return unwrap<{ subject: string; html: string; text: string | null }>(res);
}

/** Send a test email of the newsletter to the current user (admin). */
export async function testSendNewsletter(id: number): Promise<{ message: string }> {
	const res = await authPost(`${base}${api.newsletterTestSend}/${id}/test-send`);
	return unwrap<{ message: string }>(res);
}

// ── Dashboard ─────────────────────────────────────────────────

/** Fetch aggregated dashboard statistics (admin). */
export async function getDashboardStats(): Promise<DashboardStats> {
	const res = await authFetch(`${base}${api.dashboardStats}`);
	return unwrap<DashboardStats>(res);
}

/** Dismiss the system error alert on the admin dashboard. */
export async function dismissDashboardErrors(): Promise<void> {
	const res = await authPost(`${base}${api.dashboardDismissErrors}`, {});
	await unwrap(res);
}

/** Fetch grid-bucketed session locations for the analytics map (admin). */
export async function getSessionLocationAnalytics(since: string = '30d'): Promise<SessionLocationAnalytics> {
	const res = await authFetch(`${base}${api.dashboardSessionLocations}?since=${since}`);
	return unwrap<SessionLocationAnalytics>(res);
}

// ═══════════════════════════════════════════════════════════════
//  NEWS FEED (authenticated)
// ═══════════════════════════════════════════════════════════════

/** Fetch proxied WordPress news feed items. Returns empty array when feed is disabled. */
export async function getNewsFeed(): Promise<NewsFeedItem[]> {
	const res = await authFetch(`${base}${api.newsFeed}`);
	const data = await unwrap<{ items: NewsFeedItem[] }>(res);
	return data.items;
}

/**
 * Probe a URL or domain for a valid WordPress REST API posts endpoint.
 * Returns the resolved full URL on success.
 */
export async function probeNewsFeedUrl(url: string): Promise<string> {
	const res = await authPost(`${base}${api.newsFeedProbe}`, { url });
	const data = await unwrap<{ url: string }>(res);
	return data.url;
}

// ═══════════════════════════════════════════════════════════════
//  LICENSING (self-service)
// ═══════════════════════════════════════════════════════════════

/**
 * List the licensing applications with a summary of the caller's standing in each.
 *
 * An application whose backend is unreachable comes back with `summary: null` and
 * an `error` rather than failing the call, so one product being down still leaves
 * the other visible.
 */
export async function listLicenseApplications(): Promise<LicenseApplicationOverview[]> {
	const res = await authFetch(`${base}${api.licensingApps}`);
	return unwrap<LicenseApplicationOverview[]>(res);
}

/** List the caller's licences for one application. */
export async function listLicenses(
	appUUID: string,
	params: { limit?: number; offset?: number; sortBy?: string; sortDir?: string; search?: string } = {}
): Promise<PaginatedResponse<License>> {
	const res = await authFetch(`${base}${api.licensing}/${appUUID}/licenses${qs(params)}`);
	return unwrapPaginated<License>(res);
}

/** Read one licence, including its seat assignments and capabilities. */
export async function getLicense(appUUID: string, licenseUUID: string): Promise<License> {
	const res = await authFetch(`${base}${api.licensing}/${appUUID}/licenses/${licenseUUID}`);
	return unwrap<License>(res);
}

/** List the machines currently holding seats of one licence. */
export async function listLicenseActivations(
	appUUID: string,
	licenseUUID: string,
	params: { limit?: number; offset?: number; sortBy?: string; sortDir?: string } = {}
): Promise<PaginatedResponse<LicenseActivation>> {
	const res = await authFetch(`${base}${api.licensing}/${appUUID}/licenses/${licenseUUID}/activations${qs(params)}`);
	return unwrapPaginated<LicenseActivation>(res);
}

/** Release the seats held by the given machines. */
export async function deactivateLicenseActivations(appUUID: string, activationUUIDs: string[]): Promise<number> {
	const res = await authDelete(`${base}${api.licensing}/${appUUID}/activations`, { activationUUIDs });
	const data = await unwrap<{ revoked: number }>(res);
	return data.revoked;
}

/**
 * Replace the seat assignments of one licence with exactly the set supplied.
 *
 * An assignment omitted from the list is removed — this is a replace, not a patch,
 * so callers send the complete set every time.
 */
export async function updateLicenseSeats(
	appUUID: string,
	licenseUUID: string,
	assignments: SeatAssignmentInput[]
): Promise<License> {
	const res = await authPut(`${base}${api.licensing}/${appUUID}/licenses/${licenseUUID}/seats`, { assignments });
	return unwrap<License>(res);
}

/**
 * Test a licensing backend (admin).
 *
 * Takes a URL rather than an application so a backend can be verified before it is
 * saved against one. An unreachable backend is a `reachable: false` result, not a
 * rejected promise — that is the answer the test exists to give.
 */
export async function probeLicenseBackend(url: string, adapter?: string): Promise<LicenseBackendProbe> {
	const res = await authPost(`${base}${api.licensingProbe}`, { url, ...(adapter ? { adapter } : {}) });
	return unwrap<LicenseBackendProbe>(res);
}

/** List the files the caller's licences entitle them to download. */
export async function listLicenseDownloads(
	appUUID: string,
	params: { limit?: number; offset?: number; search?: string } = {}
): Promise<PaginatedResponse<LicenseDownload>> {
	const res = await authFetch(`${base}${api.licensing}/${appUUID}/downloads${qs(params)}`);
	return unwrapPaginated<LicenseDownload>(res);
}

/**
 * Get a short-lived signed link for one file.
 *
 * Minted per request rather than embedded in the listing: the link expires, and
 * the licensing backend records the download against the caller as it issues one.
 */
export async function getLicenseDownloadURL(
	appUUID: string,
	fileRecordUUID: string,
	versionID?: string
): Promise<string> {
	const res = await authFetch(
		`${base}${api.licensing}/${appUUID}/downloads/${fileRecordUUID}/url${qs({ versionID })}`
	);
	const data = await unwrap<{ url: string }>(res);
	return data.url;
}

// ═══════════════════════════════════════════════════════════════
//  SUPPORT DESK — CUSTOMER
// ═══════════════════════════════════════════════════════════════

/**
 * Builds the multipart body of a ticket or a reply.
 *
 * Fields whose value is `undefined` or blank are left out rather than sent empty,
 * so an unset optional field reaches the server as absent — the same thing the
 * JSON endpoints do by omitting the key. Files are appended under `files`, the
 * repeated field name the server's multipart parser collects into an array.
 */
function supportForm(fields: Record<string, string | undefined>, files: File[]): FormData {
	const form = new FormData();
	for (const [name, value] of Object.entries(fields)) {
		if (value !== undefined && value !== '') form.append(name, value);
	}
	for (const file of files) form.append('files', file);
	return form;
}

/** The URL of one customer-side ticket route. Keys are encoded; they are user-visible text. */
function supportTicketURL(ticketKey: string, suffix = ''): string {
	return `${base}${api.supportTickets}/${encodeURIComponent(ticketKey)}${suffix}`;
}

/** The URL of one agent-side ticket route. */
function supportDeskTicketURL(ticketKey: string, suffix = ''): string {
	return `${base}${api.supportDeskTickets}/${encodeURIComponent(ticketKey)}${suffix}`;
}

/**
 * Load the caller's support overview: whether they are an agent, every support-enabled
 * product with their eligibility in it, and how many of their tickets are open.
 *
 * A product whose licensing backend is unreachable comes back with `eligibility: null`
 * and an `error` rather than failing the call.
 */
export async function getSupportOverview(): Promise<SupportOverview> {
	const res = await authFetch(`${base}${api.supportOverview}`);
	return unwrap<SupportOverview>(res);
}

/** List the active ticket categories offered for a product — its own plus the global ones. */
export async function listSupportCategories(applicationUUID?: string): Promise<SupportCategory[]> {
	const res = await authFetch(`${base}${api.supportCategories}${qs({ applicationUUID })}`);
	return unwrap<SupportCategory[]>(res);
}

/**
 * List the caller's tickets, newest activity first.
 *
 * `scope` separates the caller's own tickets from those their seat holders filed
 * against a licence the caller owns (`seats`) and those they were added to as a
 * participant (`shared`); `all`, the default, is every one of them. `state` is `open` (new or awaiting someone) or
 * `closed` (resolved or closed).
 */
export async function listSupportTickets(
	params: {
		scope?: 'all' | 'mine' | 'seats' | 'shared';
		state?: 'open' | 'closed' | 'all';
		applicationUUID?: string;
		search?: string;
		limit?: number;
		offset?: number;
	} = {}
): Promise<PaginatedResponse<SupportTicketSummary>> {
	const res = await authFetch(`${base}${api.supportTickets}${qs(params)}`);
	return unwrapPaginated<SupportTicketSummary>(res);
}

/**
 * Open a ticket. The server checks the caller's eligibility for the product again.
 *
 * @param onProgress - Receives the upload's progress (0–1), for the attachments' progress bar.
 */
export async function createSupportTicket(
	input: SupportTicketInput,
	files: File[] = [],
	onProgress?: UploadProgressHandler
): Promise<SupportTicket> {
	const res = await authUploadWithProgress(`${base}${api.supportTickets}`, supportForm({ ...input }, files), onProgress);
	return unwrap<SupportTicket>(res);
}

/** Read one ticket with its public thread. */
export async function getSupportTicket(ticketKey: string): Promise<SupportTicket> {
	const res = await authFetch(supportTicketURL(ticketKey));
	return unwrap<SupportTicket>(res);
}

/**
 * Reply to a ticket as its requester. A reply to a resolved ticket reopens it.
 *
 * @param onProgress - Receives the upload's progress (0–1).
 */
export async function replySupportTicket(
	ticketKey: string,
	body: string,
	files: File[] = [],
	onProgress?: UploadProgressHandler
): Promise<SupportTicket> {
	const res = await authUploadWithProgress(supportTicketURL(ticketKey, '/messages'), supportForm({ body }, files), onProgress);
	return unwrap<SupportTicket>(res);
}

/** Close a ticket. Allowed even after the caller's licence has lapsed. */
export async function closeSupportTicket(ticketKey: string): Promise<SupportTicket> {
	const res = await authPost(supportTicketURL(ticketKey, '/close'));
	return unwrap<SupportTicket>(res);
}

/** Reopen a resolved ticket. Requires current eligibility. */
export async function reopenSupportTicket(ticketKey: string): Promise<SupportTicket> {
	const res = await authPost(supportTicketURL(ticketKey, '/reopen'));
	return unwrap<SupportTicket>(res);
}

/** Rate a resolved or closed ticket, once. */
export async function rateSupportTicket(ticketKey: string, rating: number, comment?: string): Promise<SupportTicket> {
	const res = await authPost(supportTicketURL(ticketKey, '/rating'), {
		rating,
		...(comment?.trim() ? { comment: comment.trim() } : {})
	});
	return unwrap<SupportTicket>(res);
}

/**
 * Add someone to a ticket by email address, as its requester.
 *
 * Only an existing, active account can be added; an unknown address is refused
 * with a message worth showing as it is.
 */
export async function addSupportParticipant(ticketKey: string, email: string): Promise<SupportTicket> {
	const res = await authPost(supportTicketURL(ticketKey, '/participants'), { email });
	return unwrap<SupportTicket>(res);
}

/**
 * Remove someone from a ticket — the requester removing a participant, or a
 * participant leaving.
 *
 * Resolves with the updated ticket when someone else was removed, and with
 * `null` when the caller left: they can no longer read the ticket, so the server
 * answers 204 and there is nothing to show.
 */
export async function removeSupportParticipant(ticketKey: string, userUUID: string): Promise<SupportTicket | null> {
	const res = await authDelete(supportTicketURL(ticketKey, `/participants/${encodeURIComponent(userUUID)}`));
	return (await unwrap<SupportTicket | undefined>(res)) ?? null;
}

/**
 * Download one attachment and save it under its original name.
 *
 * Fetched with the Authorization header and handed to the browser as a blob: the
 * endpoint is authenticated by header, not cookie, so a plain link would arrive
 * unauthenticated — and a token in the query string would leak a credential into
 * browser history and every proxy log on the way.
 */
export async function downloadSupportAttachment(attachment: {
	attachmentUUID: string;
	filename: string;
}): Promise<void> {
	const res = await authFetch(`${base}${api.supportAttachments}/${encodeURIComponent(attachment.attachmentUUID)}`);
	/* Checked before touching the body: an error is JSON, and saving it would hand the reader
	   a file that looks like their attachment and opens as nothing. `unwrap` throws the server's
	   message for any failed response. */
	if (!res.ok) return unwrap<void>(res);
	saveBlob(await res.blob(), attachment.filename);
}

/**
 * Fetch an attachment's bytes, for showing an image inline.
 *
 * Through `authFetch` because the JWT travels in a header, which an `<img src>`
 * cannot send; the caller turns the blob into an object URL.
 *
 * @param variant - `thumbnail` for the server's small WebP rendition of an image.
 */
export async function fetchSupportAttachmentBlob(attachmentUUID: string, variant?: 'thumbnail'): Promise<Blob> {
	const query = variant ? `?variant=${variant}` : '';
	const res = await authFetch(`${base}${api.supportAttachments}/${encodeURIComponent(attachmentUUID)}${query}`);
	/* A failure is JSON; `unwrap` throws its message rather than handing back an error as an image. */
	if (!res.ok) return unwrap<Blob>(res);
	return res.blob();
}

// ═══════════════════════════════════════════════════════════════
//  SUPPORT DESK — AGENT
// ═══════════════════════════════════════════════════════════════

/** Load the desk's headline counts: unassigned, mine, awaiting an agent, breaching, open. */
export async function getSupportDeskStats(): Promise<SupportDeskStats> {
	const res = await authFetch(`${base}${api.supportDeskStats}`);
	return unwrap<SupportDeskStats>(res);
}

/** List every support agent, for the assignee pickers. */
export async function listSupportAgents(): Promise<SupportPerson[]> {
	const res = await authFetch(`${base}${api.supportDeskAgents}`);
	return unwrap<SupportPerson[]>(res);
}

/**
 * List the agent queue.
 *
 * Statuses and priorities travel as comma-separated lists, which is the form the
 * server's filter parser takes; an empty list is the same as no filter.
 */
export async function listDeskTickets(query: SupportDeskQuery = {}): Promise<PaginatedResponse<SupportTicketSummary>> {
	const { status, priority, ...rest } = query;
	const res = await authFetch(
		`${base}${api.supportDeskTickets}${qs({
			...rest,
			status: status?.join(','),
			priority: priority?.join(',')
		})}`
	);
	return unwrapPaginated<SupportTicketSummary>(res);
}

/**
 * Open a ticket on a customer's behalf. No eligibility check; the requester must have an account.
 *
 * @param onProgress - Receives the upload's progress (0–1).
 */
export async function createDeskTicket(
	input: SupportDeskTicketInput,
	files: File[] = [],
	onProgress?: UploadProgressHandler
): Promise<SupportTicket> {
	const res = await authUploadWithProgress(
		`${base}${api.supportDeskTickets}`,
		supportForm({ ...input }, files),
		onProgress
	);
	return unwrap<SupportTicket>(res);
}

/** Read one ticket with its full thread, internal notes included. */
export async function getDeskTicket(ticketKey: string): Promise<SupportTicket> {
	const res = await authFetch(supportDeskTicketURL(ticketKey));
	return unwrap<SupportTicket>(res);
}

/**
 * Post a reply or an internal note.
 *
 * `status` sets the ticket's status in the same call, which is how "reply and
 * resolve" is one action rather than two that could half-succeed.
 *
 * @param onProgress - Receives the upload's progress (0–1).
 */
export async function replyDeskTicket(
	ticketKey: string,
	message: { body: string; isInternal: boolean; status?: SupportTicketStatus },
	files: File[] = [],
	onProgress?: UploadProgressHandler
): Promise<SupportTicket> {
	const res = await authUploadWithProgress(
		supportDeskTicketURL(ticketKey, '/messages'),
		supportForm({ body: message.body, isInternal: String(message.isInternal), status: message.status }, files),
		onProgress
	);
	return unwrap<SupportTicket>(res);
}

/** Change a ticket's status, priority, category or assignee. `null` clears the last two. */
export async function updateDeskTicket(ticketKey: string, patch: SupportTicketPatch): Promise<SupportTicket> {
	const res = await authPatch(supportDeskTicketURL(ticketKey), patch);
	return unwrap<SupportTicket>(res);
}

/** Add someone to a ticket by email address, from the desk. They must already have an active account. */
export async function addDeskParticipant(ticketKey: string, email: string): Promise<SupportTicket> {
	const res = await authPost(supportDeskTicketURL(ticketKey, '/participants'), { email });
	return unwrap<SupportTicket>(res);
}

/** Remove someone from a ticket, from the desk. */
export async function removeDeskParticipant(ticketKey: string, userUUID: string): Promise<SupportTicket> {
	const res = await authDelete(supportDeskTicketURL(ticketKey, `/participants/${encodeURIComponent(userUUID)}`));
	return unwrap<SupportTicket>(res);
}

/**
 * Erase an attachment from a ticket, from the desk — the stored file and its
 * preview go with it. Returns the ticket with the removal recorded in its thread.
 */
export async function deleteDeskAttachment(attachmentUUID: string): Promise<SupportTicket> {
	const res = await authDelete(`${base}${api.supportDeskAttachments}/${encodeURIComponent(attachmentUUID)}`);
	return unwrap<SupportTicket>(res);
}

/** Apply one status, priority or assignee change to many tickets. Returns how many changed. */
export async function bulkUpdateDeskTickets(update: {
	ticketKeys: string[];
	status?: SupportTicketStatus;
	priority?: SupportTicket['priority'];
	assigneeUUID?: string | null;
}): Promise<number> {
	const res = await authPost(`${base}${api.supportDeskTickets}/bulk`, update);
	const data = await unwrap<{ updated: number }>(res);
	return data.updated;
}

/** List reply templates — one product's and the global ones, or every one when no product is given. */
export async function listReplyTemplates(applicationUUID?: string): Promise<SupportReplyTemplate[]> {
	const res = await authFetch(`${base}${api.supportReplyTemplates}${qs({ applicationUUID })}`);
	return unwrap<SupportReplyTemplate[]>(res);
}

/** Create a reply template. */
export async function createReplyTemplate(input: SupportReplyTemplateInput): Promise<SupportReplyTemplate> {
	const res = await authPost(`${base}${api.supportReplyTemplates}`, input);
	return unwrap<SupportReplyTemplate>(res);
}

/** Update a reply template. */
export async function updateReplyTemplate(
	templateUUID: string,
	input: SupportReplyTemplateInput
): Promise<SupportReplyTemplate> {
	const res = await authPut(`${base}${api.supportReplyTemplates}/${encodeURIComponent(templateUUID)}`, input);
	return unwrap<SupportReplyTemplate>(res);
}

/** Delete a reply template. */
export async function deleteReplyTemplate(templateUUID: string): Promise<void> {
	const res = await authDelete(`${base}${api.supportReplyTemplates}/${encodeURIComponent(templateUUID)}`);
	return unwrap<void>(res);
}

// ═══════════════════════════════════════════════════════════════
//  SUPPORT DESK — ADMIN
// ═══════════════════════════════════════════════════════════════

/** Load the support desk configuration (admin). */
export async function getSupportSettings(): Promise<SupportSettings> {
	const res = await authFetch(`${base}${api.supportAdminSettings}`);
	return unwrap<SupportSettings>(res);
}

/** Save the support desk configuration (admin). */
export async function updateSupportSettings(settings: Partial<SupportSettings>): Promise<SupportSettings> {
	const res = await authPut(`${base}${api.supportAdminSettings}`, settings);
	return unwrap<SupportSettings>(res);
}

/** List every routing rule that has at least one member; a rule that is absent is empty (admin). */
export async function listSupportRouting(): Promise<SupportRoutingRule[]> {
	const res = await authFetch(`${base}${api.supportAdminRouting}`);
	return unwrap<SupportRoutingRule[]>(res);
}

/**
 * Replace one routing rule's members — the product rule when `categoryUUID` is
 * `null`, otherwise that category × product rule. Returns the rule as saved (admin).
 */
export async function setSupportRoutingRule(
	applicationUUID: string,
	categoryUUID: string | null,
	members: SupportRoutingMemberInput[]
): Promise<SupportRoutingRule> {
	const res = await authPut(`${base}${api.supportAdminRouting}/${encodeURIComponent(applicationUUID)}`, {
		categoryUUID,
		members
	});
	return unwrap<SupportRoutingRule>(res);
}

/** List every ticket category, inactive ones included (admin). */
export async function listAllSupportCategories(): Promise<SupportCategory[]> {
	const res = await authFetch(`${base}${api.supportAdminCategories}`);
	return unwrap<SupportCategory[]>(res);
}

/** Create a ticket category (admin). */
export async function createSupportCategory(input: SupportCategoryInput): Promise<SupportCategory> {
	const res = await authPost(`${base}${api.supportAdminCategories}`, input);
	return unwrap<SupportCategory>(res);
}

/** Update a ticket category (admin). */
export async function updateSupportCategory(
	categoryUUID: string,
	input: SupportCategoryInput
): Promise<SupportCategory> {
	const res = await authPut(`${base}${api.supportAdminCategories}/${encodeURIComponent(categoryUUID)}`, input);
	return unwrap<SupportCategory>(res);
}

/** Delete a ticket category (admin). */
export async function deleteSupportCategory(categoryUUID: string): Promise<void> {
	const res = await authDelete(`${base}${api.supportAdminCategories}/${encodeURIComponent(categoryUUID)}`);
	return unwrap<void>(res);
}

// ═══════════════════════════════════════════════════════════════
//  ANALYTICS (admin)
// ═══════════════════════════════════════════════════════════════

/**
 * Load the metric catalogue: every available metric, its dimensions, and the
 * granularities and filter operators the engine accepts.
 *
 * The admin screens build their query UI from this rather than hard-coding a
 * metric list, so adding a metric on the server makes it appear here with no
 * client change at all.
 */
export async function getAnalyticsCatalog(): Promise<AnalyticsCatalog> {
	const res = await authFetch(`${base}${api.analyticsCatalog}`);
	return unwrap<AnalyticsCatalog>(res);
}

/** Run one analytics query and return a series per requested metric. */
export async function runAnalyticsQuery(query: AnalyticsQueryRequest): Promise<AnalyticsQueryResponse> {
	const res = await authPost(`${base}${api.analyticsQuery}`, query);
	return unwrap<AnalyticsQueryResponse>(res);
}

/**
 * Run a whole page of analytics queries in one request.
 *
 * A dashboard tab is inherently an N+1 — twenty widgets are twenty to forty
 * queries — so the page asks for all of them at once rather than each widget
 * fetching for itself. Individual queries carry their own errors in the
 * response, so one broken widget does not blank the page.
 */
export async function runAnalyticsBatch(request: AnalyticsBatchRequest): Promise<AnalyticsBatchResponse> {
	const res = await authPost(`${base}${api.analyticsBatch}`, request);
	return unwrap<AnalyticsBatchResponse>(res);
}

/**
 * Load the distinct values of a metric field, to autocomplete a filter value.
 *
 * A failure resolves to an empty list rather than rejecting: autocomplete is a
 * convenience on top of a filter the admin can always type by hand, so a failed
 * lookup must not interrupt building a query.
 */
export async function getAnalyticsFieldValues(
	metric: string,
	field: string,
	search?: string
): Promise<AnalyticsFieldValue[]> {
	try {
		const res = await authFetch(`${base}${api.analyticsValues}${qs({ metric, field, search })}`);
		const data = await unwrap<{ Values: AnalyticsFieldValue[] }>(res);
		return data.Values ?? [];
	} catch {
		return [];
	}
}

/** Load the monthly cohort-retention matrix over the given lookback window. */
export async function getAnalyticsCohort(days: number, maxOffset?: number): Promise<CohortRetention> {
	const res = await authFetch(`${base}${api.analyticsCohort}${qs({ days, maxOffset })}`);
	return unwrap<CohortRetention>(res);
}

/** Load the shared analytics dashboard configuration. */
export async function getAnalyticsDashboard(): Promise<DashboardConfig> {
	const res = await authFetch(`${base}${api.analyticsDashboard}`);
	return unwrap<DashboardConfig>(res);
}

/** Save the shared analytics dashboard configuration. Applies for every admin. */
export async function saveAnalyticsDashboard(config: DashboardConfig): Promise<DashboardConfig> {
	const res = await authPost(`${base}${api.analyticsDashboard}`, config);
	return unwrap<DashboardConfig>(res);
}

/** Load the analytics collection settings and how far back the data actually goes. */
export async function getAnalyticsConfiguration(): Promise<AnalyticsConfigurationResponse> {
	const res = await authFetch(`${base}${api.analyticsConfiguration}`);
	return unwrap<AnalyticsConfigurationResponse>(res);
}

/** Save the analytics collection settings. An omitted field is left untouched. */
export async function saveAnalyticsConfiguration(
	configuration: Partial<AnalyticsConfiguration>
): Promise<AnalyticsConfigurationResponse> {
	const res = await authPost(`${base}${api.analyticsConfiguration}`, configuration);
	return unwrap<AnalyticsConfigurationResponse>(res);
}

/** Load a page of the raw tracked-action event stream. */
export async function listAnalyticsEvents(
	params: { limit?: number; offset?: number; query?: string } = {}
): Promise<PaginatedResponse<AnalyticsEventRow>> {
	const res = await authFetch(`${base}${api.analyticsEvents}${qs(params)}`);
	return unwrapPaginated<AnalyticsEventRow>(res);
}
